<script lang="ts">
// noinspection JSUnusedGlobalSymbols

import { I3pEventsEnum } from '@nuxtDatakodeUtils/types/I3pEvents.enum'
import DkOlVectorLayer from '@nuxtDatakodeUtils/components/dk-ol-map/layers/dk-ol-vector-layer.vue'
import type { FeatureCollection } from 'geojson'
import type { FeatureLike } from 'ol/Feature'
import type { ProjectionLike } from 'ol/proj'
import { inject } from 'vue'
import type olMap from 'ol/Map'
import type { GeoJSONGeometry } from 'ol/format/GeoJSON'
import Legend from 'ol-ext/legend/Legend'
import LegendItem from 'ol-ext/legend/Item'
import GenericLayerStyle from '~/components/I3pComponent/common/map/GenericLayerStyle.vue'
import { OpportunitiesI3pService } from '~/services/opportunities.i3p.service'
import type { Entity } from '~/models/Entity'
import { OpportunityMapService } from '~/services/opportunityMap.service'
import { OpportunityVectorLayerService } from '~/services/opportunityVectorLayer.service'

export default {
    name: 'OpportunitiesLayer',
    components: { GenericLayerStyle, DkOlVectorLayer },
    props: {
        mapService: {
            type: [OpportunityMapService, null],
            required: true,
        },
        i3pService: { type: OpportunitiesI3pService, required: true },
    },
    emits: ['hoverFeatures', 'clickFeatures'],
    data() {
        return {
            map: null as olMap,
            geojsonData: null as FeatureCollection,
            currentEntityK: null as string,
            currentFeature: null as FeatureLike,
            dataProjection: 'EPSG:4326' as ProjectionLike,
            displayGeomAsCentroidPoint: false as boolean,
        }
    },
    computed: {
        opportunityVectorLayerService() {
            return new OpportunityVectorLayerService()
        },
        layerName(): string {
            return this.i3pService.getLayerName()
        },
    },
    mounted() {
        this.map = inject('map') as olMap
        this.dataProjection = this.i3pService.getDataProjection()
        this.i3pService.emitter
            .on(I3pEventsEnum.geojsonUpdated, (event: { geojsonData: FeatureCollection }) => {
                this.geojsonData = event.geojsonData
            })

        this.i3pService.emitter
            .on(I3pEventsEnum.beforeSaveEntity, () => {
                // to avoid reset geom during save
                this.$refs.opportunitiesLayer?.saveOriginalCoordinates()
            })

        // change current entity
        this.i3pService.emitter
            .on(I3pEventsEnum.currentEntityUpdating, (event: { currentEntityK: string | number }) => {
                this.refreshCurrenEntityK(event.currentEntityK)
            })

        // current entity updated
        this.i3pService.emitter
            .on(I3pEventsEnum.currentEntityUpdated, (event: { currentEntity: Entity }) => {
                this.updateCurrenFeatureStatus(event.currentEntity)
            })

        // entity updated or created
        this.i3pService.emitter
            .on(I3pEventsEnum.entityUpdated, (event: { entity: Entity }) => {
                this.refreshCurrenEntityK(event.entity?.getKey())
            })

        // entity deleted
        this.i3pService.emitter
            .on(I3pEventsEnum.entityDeleted, () => {
                this.refreshCurrenEntityK(null)
            })
    },

    methods: {
        updateLegendVectorStyle() {
            const legend: Legend = this.mapService?.legend as Legend
            const opportunitiesLayer = this.$refs?.opportunitiesLayer?.layer
            if (!legend || !opportunitiesLayer)
                return

            const source = opportunitiesLayer.getSource()
            if (!source)
                return

            const features = source.getFeatures()
            if (!features)
                return

            const styleMap = new Map<string, { title: string, style: any }>()

            features.forEach((feature: FeatureLike) => {
                const title = feature.get('my_like')?.value
                if (!styleMap.has(title)) {
                    styleMap.set(
                        title,
                        {
                            title: this.$translate(`voirie.opportunities.my_like.value.${title}`),
                            style: this.opportunityVectorLayerService.getPolygonStyle(feature, false, false),
                        },
                    )
                }
            })

            const targetLegend = legend.getItems()?.getArray()?.find(
                (item: Legend) => item.getTitle?.() === opportunitiesLayer.get('title'),
            ) as Legend
            if (targetLegend) {
                const legendItems = targetLegend.getItems()?.getArray()
                legendItems?.forEach((item: LegendItem) => {
                    targetLegend.removeItem(item)
                })
                styleMap.values().forEach((data) => {
                    const legendItem = new LegendItem({
                        title: data.title,
                        typeGeom: 'MultiPolygone',
                        style: data.style,
                    })
                    targetLegend.addItem(legendItem)
                })
            }
            else {
                const intentionsLegend = new Legend({
                    title: opportunitiesLayer.get('title'),
                    layer: opportunitiesLayer,
                })
                legend.addItem(intentionsLegend)

                styleMap.values().forEach((data) => {
                    const legendItem = new LegendItem({
                        title: data.title,
                        typeGeom: 'MultiPolygone',
                        style: data.style,
                    })
                    intentionsLegend.addItem(legendItem)
                })
            }
        },
        refreshCurrenEntityK(k: string | number | null) {
            const hasChanged = k !== this.currentEntityK
            this.currentEntityK = k
            this.currentFeature = k ? this.$refs.opportunitiesLayer?.getFeatureByKey(k) : null
            if (this.currentFeature) {
                const features = this.$refs.opportunitiesLayer?.getFeaturesByKey(k)
                this.opportunityVectorLayerService.setSelectedFeatures(features)
            }

            if (hasChanged) {
                this.centerOnCurrentEntity()
            }
        },
        updateCurrenFeatureStatus(entity: Entity) {
            const feature = this.currentFeature
            const data = entity?.data
            const key = 'status'
            if (feature && data && feature[key] !== data[key] && data[key]) {
                this.currentFeature.set('status', data[key])
            }
            this.updateLegendVectorStyle()
        },
        centerOnCurrentEntity(): void {
            if (!this.currentFeature) {
                return
            }
            switch (this.i3pService.getNavigationMode()) {
                case 'zoomOnItem':
                    this.$refs.opportunitiesLayer?.centerOnFeature(this.currentFeature, true, 18)
                    break
                case 'displayAll':
                    this.$refs.opportunitiesLayer?.centerOnFeatureIfOutOfScope(this.currentFeature)
                    break
            }
        },
        displayAll(): void {
            this.$refs.opportunitiesLayer?.displayAll(17)
        },
        hoverFeatures(features: FeatureLike[]) {
            this.$emit('hoverFeatures', { features })
        },
        clickFeatures(features: FeatureLike[]) {
            this.$emit('clickFeatures', { features })
        },
        updateGeometry(event: { geometry: SimpleGeometry | null, reprojectedGeometry: GeoJSONGeometry | null }) {
            if (!event.geometry) {
                this.i3pService.triggerGeometryChanged(null)
                return
            }

            // transform geometry to multiple
            const geometry = this.opportunityVectorLayerService.forceMultiGeoJSONGeometry(event.reprojectedGeometry as GeoJSONGeometry)
            console.warn(`update Geom: ${JSON.stringify(geometry)}`)
            this.i3pService.triggerGeometryChanged(event.reprojectedGeometry)
        },
    },

}
</script>

<template>
    <template v-if="geojsonData">
        <DkOlVectorLayer
            ref="opportunitiesLayer"
            :geojson-data
            :title="layerName"
            :vector-layer-service="opportunityVectorLayerService"
            :selected-features="currentFeature ? [currentFeature] : []"
            :display-geom-as-centroid-point
            @hover-features="hoverFeatures($event)"
            @click-features="clickFeatures($event)"
            @update-geometry="updateGeometry($event)"
            @load-json-done="centerOnCurrentEntity()"
        >
            <GenericLayerStyle
                ref="layerStyle"
                :vector-layer-service="opportunityVectorLayerService"
            />
        </DkOlVectorLayer>
    </template>
</template>
